import React from 'react'
import type { Coach } from '../../api/client'
import type { Forecast, GoalNumbers } from '../../plan/forecast'
import {
  MONTH_NEEDS_PLAN,
  MONTH_NOT_LIVE,
  DEMAND_LIMIT,
  DROP_DAY_TRY,
  WEEK_LEAD,
  actionWorth,
  formatPounds,
  gapLine,
  goalScaleLabel,
  hoursPerMonth,
  leverLines,
  limitLine,
  monthCostsLine,
  monthLead,
  monthPaceNote,
  monthSoFarLabel,
  monthTakeHome,
  monthTickLine,
  weekBarLine,
  weekGapNote,
  workingLine,
} from '../../plan/wording'
import { Icon } from '../../ui/Icon'
import Verdict from '../../feedback/Verdict'

interface Props {
  forecast: Forecast
  goal: GoalNumbers
  // tip is the freedom suggestion for a week over its goal, with the change
  // it makes when she tries it.
  tip?: { text: string; onTry: () => void } | null
}

interface MonthProps {
  goal: GoalNumbers
  coach: Coach | null
  coachFailed: boolean
  onRetryCoach: () => void
  editing: boolean
  now: Date
}

// fraction is part/whole kept between 0 and 1, for a bar's width.
const fraction = (part: number, whole: number) => (whole > 0 ? Math.min(1, Math.max(0, part / whole)) : 0)
const percent = (n: number) => `${Math.round(n * 100)}%`

// PlanResult sits at the foot of the Dream Week card: what that week would
// take home a month against the goal, with the working behind it, so cause
// and effect share one card. It says in words what its number is; the bar is
// decorative, green once the goal is reached, with a £0-to-goal scale under
// it. Over the goal, a tip names a day she could drop and still reach it,
// with Try it. The month itself is MonthSection's.
export default function PlanResult({ forecast: f, goal, tip }: Props) {
  const [open, setOpen] = React.useState(false)
  const target = goal.take_home_target_pence
  const costs = goal.monthly_costs_pence
  const week = weekBarLine(f)
  const note = weekGapNote(f, target)
  const gap = gapLine(f, target)
  const levers = leverLines(f)
  // Over the goal the bar runs to the week's take-home, with the goal marked
  // where it falls, so more take-home still moves it.
  const span = Math.max(target, f.take_home_pence)
  const goalAt = fraction(target, span)
  const scale = <Scale target={target} goalAt={goalAt} />
  // When clients are the limit, more hours change nothing here: say so
  // under the bar, not only in the working.
  const demandLimited = f.limit === 'demand' && f.demand_known

  return (
    <div className="plan-result">
      <section className="progress-section">
        <p className="progress-lead">{WEEK_LEAD}</p>
        <div className="progress-row">
          {/* e2e reads the week-amount as a number: the pounds alone. */}
          <span className="progress-amount">
            <span data-testid="week-amount">{formatPounds(Math.max(0, f.take_home_pence))}</span>
            <span className="progress-amount__unit"> a month</span>
          </span>
          {note.text && (
            <span className={`progress-aside${note.reached ? ' progress-aside--reached' : ''}`}>
              {note.reached && <Icon name="check" size={16} />}
              {note.text}
            </span>
          )}
        </div>
        <div className="progress-bar" aria-hidden="true">
          <div className={`progress-bar__fill progress-bar__fill--week${note.reached ? ' progress-bar__fill--reached' : ''}`} style={{ width: percent(fraction(f.take_home_pence, span)) }} />
          <div className={`progress-bar__goal${goalAt < 1 ? ' progress-bar__goal--inside' : ''}`} style={{ left: percent(goalAt) }} />
        </div>
        {scale}
        {f.limit === 'no_hours' && <p className="progress-line">{week.text}</p>}
        {demandLimited && <p className="progress-line">{DEMAND_LIMIT}</p>}
        {tip && (
          <div className="plan-tip">
            <p>{tip.text}</p>
            <button type="button" className="btn btn-secondary btn-compact" onClick={tip.onTry}>
              {DROP_DAY_TRY}
            </button>
          </div>
        )}
        <button type="button" className="btn btn-quiet btn-compact" aria-expanded={open} onClick={() => setOpen(!open)}>
          How is this worked out?
        </button>
        {open && (
          <div className="progress-working">
            {f.limit !== 'no_hours' && !demandLimited && <span className="forecast-footer__limit">{limitLine(f)}</span>}
            {gap && <p className="forecast-footer__gap">{gap}</p>}
            {levers.length > 0 && (
              <ul className="forecast-footer__levers">
                {levers.map((line) => (
                  <li key={line}>{line}</li>
                ))}
              </ul>
            )}
            {f.limit !== 'no_hours' && (
              <dl className="forecast-footer__working">
                <div>
                  <dt>How</dt>
                  <dd>{workingLine(f, costs)}</dd>
                </div>
                <div>
                  <dt>Appointments a month</dt>
                  <dd>{Math.round(f.visits)}</dd>
                </div>
                <div>
                  <dt>Hours a month</dt>
                  <dd>{hoursPerMonth(f)}</dd>
                </div>
              </dl>
            )}
          </div>
        )}
      </section>

    </div>
  )
}

// Scale is the £0-to-goal line under a bar. When the bar runs past the goal
// (goalAt below 1), the goal's label sits under its marker instead of at
// the end.
function Scale({ target, goalAt = 1 }: { target: number; goalAt?: number }) {
  return (
    <div className="progress-scale" aria-hidden="true">
      <span>£0</span>
      {goalAt < 1 ? (
        <span className="progress-scale__goal" style={{ left: percent(goalAt) }}>
          {goalScaleLabel(target)}
        </span>
      ) : (
        <span>{goalScaleLabel(target)}</span>
      )}
    </div>
  )
}

// MonthSection, in the This week card, is where the plan meets the diary:
// this month's real take-home against the same goal, and the coach's reading
// of the gap. Before there's a month to show, one line says what will appear.
export function MonthSection({ goal, coach, coachFailed, onRetryCoach, editing, now }: MonthProps) {
  const target = goal.take_home_target_pence
  const costs = goal.monthly_costs_pence
  // The month and the coach need both a live diary and a saved goal to
  // measure it against; the API sends no progress until both are there.
  const progress = coach?.started ? coach.progress : null
  const month = progress ? monthTakeHome(progress, costs) : null
  const scale = <Scale target={target} />

  return (
    <div className="month-section">
      <h3 className="this-week__label">{monthSoFarLabel(now)}</h3>
      {month && (
        <section className={`progress-section progress-month${editing ? ' progress-month--faded' : ''}`}>
          <p className="progress-lead">{monthLead(costs)}</p>
          <div className="progress-row">
            <span className="progress-amount">{formatPounds(month.soFarPence)}</span>
            {month.costsCovered && <span className="progress-aside">{monthPaceNote(month)}</span>}
          </div>
          <div className="progress-bar" aria-hidden="true">
            <div className="progress-bar__fill progress-bar__fill--month" style={{ width: percent(fraction(month.soFarPence, target)) }} />
            {month.costsCovered && <div className="progress-bar__tick" style={{ left: percent(fraction(month.onPacePence, target)) }} />}
            <div className="progress-bar__goal" />
          </div>
          {scale}
          {/* The tick only shows once costs are covered, and so does what it means. */}
          <p className="progress-line">{month.costsCovered ? monthTickLine(now) : monthCostsLine(month, costs)}</p>
        </section>
      )}
      {coach && !progress && <p className="goals-muted progress-note">{coach.started ? MONTH_NEEDS_PLAN : MONTH_NOT_LIVE}</p>}
      {!coach && coachFailed && (
        <div className="progress-note">
          <p className="goals-muted">Couldn’t load your coach. Check your connection and try again.</p>
          <button type="button" className="btn btn-quiet btn-compact" onClick={onRetryCoach}>
            Try again
          </button>
        </div>
      )}
      {!coach && !coachFailed && <p className="goals-muted progress-note">Looking at your month…</p>}

      {coach && progress && (
        <div className="progress-coach">
          <Verdict key={coach.summary_key} itemKey={coach.summary_key} given={coach.summary_verdict} reasons={false}
            lead={<span className="goal-label">Your coach</span>} />
          <p className="coach-summary">{coach.summary}</p>
          {coach.actions.length > 0 && (
            <ul className="coach-actions">
              {coach.actions.map((a) => (
                <li key={a.id}>
                  <span className="coach-action__title">{a.title}</span>
                  <span className="coach-action__why">{a.why}</span>
                  <Verdict key={a.key} itemKey={a.key} given={a.verdict}
                    lead={coach.generated && actionWorth(a) ? <span className="coach-action__worth">{actionWorth(a)}</span> : undefined} />
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  )
}
